import type { ReactElement } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import {
  Card,
  KPIValue,
  Skeleton,
  StatusDot,
  StatusPill,
  cn,
} from "@playbookai/ui";
import { seriesDot } from "./series-style.js";
import { formatCount, type AdoptionModuleView } from "./adoption-view.js";

function Change({ reading }: { reading: AdoptionModuleView }): ReactElement {
  if (reading.availability === "never-enabled") {
    return <StatusPill tone="neutral">Not enabled</StatusPill>;
  }
  if (reading.status === "unavailable") {
    return <StatusPill tone="neutral">Unavailable</StatusPill>;
  }
  if (reading.availability === "disabled") {
    return <StatusPill tone="neutral">Disabled now</StatusPill>;
  }
  const change = reading.changePercent;
  if (change === null) {
    return (
      <span className="text-xs text-pb-text-muted">
        New activity from a zero baseline
      </span>
    );
  }
  if (change === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-pb-text-muted">
        <Minus className="size-3.5" aria-hidden /> No change
      </span>
    );
  }
  const rising = change > 0;
  const Icon = rising ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs font-medium",
        rising ? "text-pb-green-text" : "text-pb-amber-text",
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {Math.abs(change)}% {rising ? "higher" : "lower"}
    </span>
  );
}

export function MetricStrip({
  readings,
  loading,
}: {
  readings: readonly AdoptionModuleView[];
  loading: boolean;
}): ReactElement {
  return (
    <Card>
      <div className="grid divide-y divide-pb-border/70 sm:grid-cols-2 sm:divide-x sm:divide-y-0 @min-[58.5rem]/settings-page:grid-cols-4">
        {loading
          ? Array.from({ length: 4 }, (_, index) => (
              <div className="space-y-3 p-5" key={index}>
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-9 w-16" />
                <Skeleton className="h-4 w-36" />
              </div>
            ))
          : readings.map((reading) => (
              <article className="min-w-0 p-5" key={reading.id}>
                <div className="flex items-center gap-2">
                  <StatusDot {...seriesDot(reading.id)} />
                  <h2 className="truncate text-sm font-medium text-pb-text-secondary">
                    {reading.label}
                  </h2>
                </div>
                {/* Look row SN-16: the 30px hero figure is KPIValue lg (32px). */}
                <KPIValue
                  className="mt-2"
                  size="lg"
                  value={
                    reading.availability === "never-enabled"
                      ? "—"
                      : formatCount(reading.total)
                  }
                />
                <div className="mt-2 min-h-5">
                  <Change reading={reading} />
                </div>
                <p className="mt-3 text-xs leading-relaxed text-pb-text-muted">
                  {reading.description}
                </p>
              </article>
            ))}
      </div>
    </Card>
  );
}
